/**
 * The routes.
 *
 * Everything is either signing in or signed in, and `RequireAuth` is what
 * separates the two. It is a usability boundary rather than a security one —
 * every `/api/v1` route is enforced server-side by `get_current_user`, and
 * `/api/v1/admin/*` again by `require_role(Role.SUPERADMIN)`. What the guards
 * below buy is a comprehensible answer instead of a 403 the page cannot
 * explain, and one place where "this account cannot be used yet" is decided.
 *
 * The `/login` route holds the `mfaToken` that `LoginPage` produces and
 * `TwoFactorPage` consumes. That state deliberately lives here rather than
 * inside either screen: a correct password is *not* a session for an account
 * with 2FA — the server issues no cookies until `/auth/2fa/verify` accepts a
 * code — so the token has to survive the swap between the two components
 * without either of them being able to mistake it for one.
 */
import { useState } from 'react'
import type { ReactNode } from 'react'
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom'

import AdminPage from './account/AdminPage'
import { EnrollmentStep } from './account/EnrollmentStep'
import InviteAcceptPage from './account/InviteAcceptPage'
import ProfilePage from './account/ProfilePage'
import { LoginPage } from './auth/LoginPage'
import { RequireAuth } from './auth/RequireAuth'
import { TwoFactorPage } from './auth/TwoFactorPage'
import { useAuth } from './auth/useAuth'
import { DashboardPage } from './dashboard/DashboardPage'
import { AppShell, SUPERADMIN } from './nav/AppHeader'

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginRoute />} />
      {/* Outside RequireAuth on purpose: an invitee has no account yet, so the
          guard would bounce them to a sign-in form they cannot use — which is
          exactly what the missing route used to do, silently dropping the one
          token that could ever have created their account. */}
      <Route path="/invite" element={<InviteRoute />} />
      <Route
        path="/"
        element={
          <RequireAuth>
            <PasswordRotationGate>
              <DashboardPage />
            </PasswordRotationGate>
          </RequireAuth>
        }
      />
      <Route
        path="/profile"
        element={
          <RequireAuth>
            {/* No gate here: /profile is where the gate sends people. */}
            <AppShell>
              <ProfilePage />
            </AppShell>
          </RequireAuth>
        }
      />
      <Route
        path="/admin"
        element={
          <RequireAuth>
            <PasswordRotationGate>
              <RequireSuperadmin>
                {/* Wide: the team table is six columns plus three action
                    buttons and does not fit a form-width column. */}
                <AppShell wide>
                  <AdminPage />
                </AppShell>
              </RequireSuperadmin>
            </PasswordRotationGate>
          </RequireAuth>
        }
      />
      {/* Three pages, a login screen and an invite redemption screen; anything
          else is a mistyped URL. */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

function LoginRoute() {
  const { status, reload } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [mfaToken, setMfaToken] = useState<string | null>(null)
  /**
   * The other half-authenticated token, held here for the same reason as
   * `mfaToken`: a correct password on an account that owes a second factor is
   * not a session — the server sets no cookies — and neither screen may be able
   * to mistake the token for one. It becomes a session only when
   * `/auth/2fa/enroll/confirm` accepts a code.
   */
  const [enrollmentToken, setEnrollmentToken] = useState<string | null>(null)
  /** Who is enrolling. `/auth/login` never echoes the address back, so this is
   *  the one the user typed -- and the enrollment screen needs it: it is where
   *  a QR is scanned and one irreplaceable set of backup codes is burned. */
  const [enrollmentEmail, setEnrollmentEmail] = useState<string | undefined>(undefined)

  // `RequireAuth` records where the user was headed before it bounced them.
  const from = (location.state as { from?: string } | null)?.from ?? '/'

  if (status === 'authenticated') return <Navigate to={from} replace />

  /**
   * Cookies are set at this point but nothing in the app knows it yet: the
   * session is httpOnly, so the only way to learn who is signed in is to ask
   * `/auth/me` again. Navigating first would land on a dashboard that thinks it
   * is anonymous and bounce straight back here.
   */
  async function finish() {
    await reload()
    navigate(from, { replace: true })
  }

  // Not skippable: no cancel here, unlike the 2FA step. A cancel would only
  // return the user to a sign-in form that hands back the same requirement with
  // a fresh token, so the honest control is none at all.
  //
  // `onExpired` is not that control. The token lives ten minutes -- about how
  // long it takes to go and install an authenticator app -- and once it dies
  // every call the screen can make 401s. Dropping it here is what makes the
  // form reachable again; the login behind it re-issues the requirement.
  if (enrollmentToken !== null) {
    return (
      <EnrollmentStep
        enrollmentToken={enrollmentToken}
        email={enrollmentEmail}
        onDone={() => void finish()}
        onExpired={() => setEnrollmentToken(null)}
      />
    )
  }

  if (mfaToken === null) {
    return (
      <LoginPage
        onMfaRequired={setMfaToken}
        onEnrollmentRequired={(token, email) => {
          setEnrollmentEmail(email)
          setEnrollmentToken(token)
        }}
        onAuthenticated={() => void finish()}
      />
    )
  }

  return (
    <TwoFactorPage
      mfaToken={mfaToken}
      onVerified={() => void finish()}
      // A dead pending token cannot be retried — there is nothing to retry
      // with — so the user goes back to the password step.
      onExpired={() => setMfaToken(null)}
      onCancel={() => setMfaToken(null)}
    />
  )
}

/**
 * Redeeming an invite, then enrolling, then landing in the app.
 *
 * The route owns the last step for the same reason `LoginRoute` does: the
 * session that `/auth/2fa/enroll/confirm` starts lives in httpOnly cookies, so
 * `/auth/me` has to be re-read before anything navigates — otherwise the
 * dashboard mounts believing it is anonymous and `RequireAuth` bounces it
 * straight back out.
 */
function InviteRoute() {
  const { reload } = useAuth()
  const navigate = useNavigate()

  return (
    <InviteAcceptPage
      onEnrolled={() => {
        void (async () => {
          await reload()
          navigate('/', { replace: true })
        })()
      }}
      // The invite token was spent creating the account, so reloading /invite
      // only re-renders a form that can now answer nothing but "invalid or
      // expired invite". The sign-in form is the only door left, and it hands
      // back a fresh enrollment token for the same gate.
      onEnrollmentExpired={() => navigate('/login', { replace: true })}
    />
  )
}

/**
 * The forced password rotation.
 *
 * `must_change_password` is set on every invited account and on the bootstrap
 * superadmin, and until `POST /auth/password` clears it the credential in use
 * is one somebody else typed out and sent. So a user carrying the flag gets
 * exactly one page: the profile, where the form that clears it lives.
 *
 * Three things this deliberately does not do. It does not sit on `/profile`,
 * so clearing the flag releases the user rather than re-trapping them there.
 * It does not touch `/login`, so signing out still works — `RequireAuth` sees
 * the anonymous session first and this never renders. And it does not pretend
 * to be enforcement: the server still refuses nothing on the strength of the
 * flag, so this is about not stranding a user in front of a page they cannot
 * yet use, not about holding a line.
 */
function PasswordRotationGate({ children }: { children: ReactNode }) {
  const { user } = useAuth()

  if (user?.must_change_password) return <Navigate to="/profile" replace />

  return <>{children}</>
}

/**
 * The admin section, for superadmins only.
 *
 * A member who follows a stale bookmark to `/admin` lands on the dashboard.
 * That is the honest answer for them — the page would 403 on its first request
 * and have nothing to show — and it is not the security control; that is
 * `require_role(Role.SUPERADMIN)` on the router itself.
 */
function RequireSuperadmin({ children }: { children: ReactNode }) {
  const { user } = useAuth()

  if (user?.role !== SUPERADMIN) return <Navigate to="/" replace />

  return <>{children}</>
}
